import React from 'react';

import { types } from '../..';
import { resourcesSearch } from '../../helpers';
import { mui } from '../../libs';

import { CustomOption, IResourceAutocompleteProps, ValueType } from '.';

/**
 * A simple autocomplete component that receives options as prop.
 * This is a simplified version of ResourceAutocomplete that doesn't depend on Redux state.
 */
const Simplified = <
  T extends resourcesSearch.ReturnItem = types.common.ResourceExpanded,
  Multiple extends boolean | undefined = undefined,
  ChooseHandle extends boolean | undefined = undefined,
>(
  props: IResourceAutocompleteProps<T, Multiple, ChooseHandle>
) => {
  const {
    value,
    setValue,
    customOptions,
    disabled = false,
    enableNone = false,
    noneLabel = 'None',
    getOptionLabel: customGetOptionLabel,
    getOptionDisabled,
    groupBy,
    autocompleteProps = {},
    inputProps = {},
  } = props;

  const { multiple } = autocompleteProps;

  const None = noneLabel;

  const allOptions = React.useMemo(() => {
    if (enableNone && !multiple) {
      return [{ id: null, name: None } as CustomOption, ...customOptions];
    }
    return customOptions;
  }, [customOptions, enableNone, multiple, None]);

  const handleChange = (_event: React.SyntheticEvent, newValue: T | T[] | null) => {
    if (multiple) {
      const val = (newValue as T[] | null) || [];
      setValue(val.map((r) => r.id) as ValueType<Multiple, ChooseHandle>);
    } else {
      const singleVal = newValue as T | null;
      const idVal = !singleVal?.id || singleVal.id === null ? null : (singleVal.id as number);
      setValue(idVal as ValueType<Multiple, ChooseHandle>);
    }
  };

  const getValue = (): CustomOption | CustomOption[] | null => {
    if (multiple) {
      const ids = (value as number[]) || [];
      return ids.map((id) => allOptions.find((o) => o.id === id)).filter(Boolean) as T[];
    }
    const selected = allOptions.find((o) => o.id === value);
    if (enableNone && !selected) return { id: null, name: None } as T;
    return selected || null;
  };

  const selectedValue = React.useMemo(() => getValue(), [value, allOptions, enableNone, multiple, None]);

  const defaultGetOptionLabel = (option: T): string => {
    if (!option) return '';
    if (option.name && option.handle) return `${option.name} (${option.handle})`;
    return option.name || '';
  };

  const getOptionLabelFn = customGetOptionLabel || defaultGetOptionLabel;

  const isOptionEqualToValue = (option: T, val: T) => option?.id === val?.id;

  const filterOptions = (opts: T[], state: { inputValue: string }) => {
    const searchTerm = state.inputValue.toLowerCase();
    if (!searchTerm) return opts;

    return opts.filter((option) => {
      const name = option.name?.toLowerCase() || '';
      const handle = option.handle?.toLowerCase() || '';
      return name.includes(searchTerm) || handle.includes(searchTerm);
    });
  };

  return (
    <mui.core.Autocomplete<T, Multiple, boolean, boolean>
      {...(autocompleteProps as any)}
      multiple={multiple}
      options={allOptions}
      value={selectedValue as any}
      onChange={handleChange}
      disabled={disabled}
      disableClearable={!enableNone}
      openOnFocus
      filterOptions={filterOptions}
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionLabel={getOptionLabelFn}
      getOptionDisabled={getOptionDisabled}
      groupBy={groupBy}
      renderOption={(props, option) => (
        <li
          {...props}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            padding: '8px 12px',
          }}
        >
          <mui.core.Typography variant="body2" sx={{ fontWeight: 500 }}>
            {option.name}
          </mui.core.Typography>
          {option.handle && option.id !== null && (
            <mui.core.Typography variant="caption" color="text.secondary">
              {option.handle}
            </mui.core.Typography>
          )}
        </li>
      )}
      renderInput={(params) => <mui.core.TextField {...params} {...inputProps} />}
    />
  );
};

export default Simplified;
